import { Link } from 'react-router-dom';
import { parseDateSafe, formatDisplayShort } from '../../../lib/dateUtils';
import { assetDisplayLabel } from '../../../lib/formatUtils';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';

/** Skeleton row for loading state – mimics table row shape to prevent layout shift. */
function AssetRowSkeleton({ showCheckbox }) {
  return (
    <tr className="asset-row-skeleton">
      {showCheckbox && <td className="asset-col-checkbox"><div className="asset-skeleton-block" style={{ width: '18px', margin: '0 auto' }} /></td>}
      <td><div className="asset-skeleton-block" /></td>
      <td><div className="asset-skeleton-block" /></td>
      <td><div className="asset-skeleton-block" /></td>
      <td><div className="asset-skeleton-block" /></td>
      <td><div className="asset-skeleton-block" /></td>
      <td><div className="asset-skeleton-block" /></td>
      <td><div className="asset-skeleton-block" /></td>
      <td><div className="asset-skeleton-block" /></td>
    </tr>
  );
}

/**
 * Controlled desktop assets table. All state, mutations, and data fetching stay
 * in the parent (Assets.jsx); this component only renders rows and forwards
 * events. Selection set logic (add/remove) is owned by the parent via the
 * onSelectId / onSelectAll callbacks.
 */
export default function AssetTableDesktop({
  assets,
  selectedIds,
  canManageAssets,
  conditionRatings,
  snakeToTitleCase,
  showInitialSkeleton,
  showMoreSkeleton,
  onSelectId,
  onSelectAll,
  onRecordTest,
  onEdit,
  onDelete,
}) {
  const allVisibleSelected = assets.length > 0 && assets.every((a) => selectedIds.has(a.id));

  return (
    <div className="assets-table-desktop">
      <table className="data-table">
        <thead>
          <tr>
            {canManageAssets && (
              <th className="asset-col-checkbox">
                <input
                  type="checkbox"
                  className="asset-row-checkbox"
                  checked={allVisibleSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  title={allVisibleSelected ? 'Deselect all visible' : 'Select all visible'}
                />
              </th>
            )}
            <th>Asset / Equipment Code</th>
            <th>Site</th>
            <th>Building</th>
            <th>Room</th>
            <th>Type</th>
            <th>Status</th>
            <th>Next Test</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {showInitialSkeleton ? (
            Array.from({ length: 5 }).map((_, i) => <AssetRowSkeleton key={`skeleton-initial-${i}`} showCheckbox={canManageAssets} />)
          ) : (
            <>
              {(assets ?? []).map((asset) => {
                const nextTestDate = asset.next_test_date ? parseDateSafe(asset.next_test_date) : null;
                const isOverdue = nextTestDate && nextTestDate < new Date();
                return (
                  <tr key={asset.id} className={`asset-row-fade${selectedIds.has(asset.id) ? ' asset-row-selected' : ''}`}>
                    {canManageAssets && (
                      <td className="asset-col-checkbox">
                        <input
                          type="checkbox"
                          className="asset-row-checkbox"
                          checked={selectedIds.has(asset.id)}
                          onChange={(e) => onSelectId(asset.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    <td>{assetDisplayLabel(asset) || snakeToTitleCase(asset.asset_type) || '-'}</td>
                    <td>
                      <Link to={`/sites`} className="link">
                        {asset.site_display_name ?? asset.site_name ?? '-'}
                      </Link>
                    </td>
                    <td>{asset.building ?? '-'}</td>
                    <td>{asset.room ?? '-'}</td>
                    <td>
                      {snakeToTitleCase(asset.asset_type) || '-'}
                      {asset.asset_type === 'workshop_equipment' && (
                        <span className="asset-type-badge asset-type-fitments" title="Fitments"> (Fitments)</span>
                      )}
                    </td>
                    <td>
                      {asset.condition_rating != null && (
                        <Badge variant="muted" style={{ marginRight: '4px' }}>
                          {conditionRatings.find((c) => c.value === asset.condition_rating)?.label ?? `Cond. ${asset.condition_rating}`}
                        </Badge>
                      )}
                      <Badge
                        variant={asset.status === 'removed' || asset.status === 'inactive' ? 'muted' : 'success'}
                      >
                        {asset.status === 'removed' ? 'Removed' :
                         asset.status === 'inactive' ? 'Inactive' :
                         'Active'}
                      </Badge>
                    </td>
                    <td>
                      {nextTestDate ? (
                        <span className={isOverdue ? 'next-test-overdue' : ''}>
                          {formatDisplayShort(nextTestDate)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <div className="asset-actions asset-actions-stacked">
                        <Button type="button" size="sm" onClick={() => onRecordTest(asset)}>
                          Record Test
                        </Button>
                        <Link to={`/tests?tab=history&assetId=${asset.id}`} className="btn btn-sm asset-history-btn">
                          History
                        </Link>
                        <Button type="button" size="sm" onClick={() => onEdit(asset)}>
                          Edit
                        </Button>
                        {/* RBAC: Delete (Global) hidden when user.role === 'technician' */}
                        {canManageAssets && (
                          <Button type="button" variant="danger" size="sm" onClick={() => onDelete(asset)}>
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {showMoreSkeleton && (
                Array.from({ length: 5 }).map((_, i) => <AssetRowSkeleton key={`skeleton-more-${i}`} showCheckbox={canManageAssets} />)
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
